import type { LucideIcon } from 'lucide-react'
import { createContext, useContext } from 'react'
import { cn } from '../lib/cn'

/**
 * The icon of the part of the app a page belongs to (its entry in the sidebar), set by the app's
 * frame for the page it shows: page headers and record cards carry it, so every screen says at a
 * glance where it is without each page naming an icon.
 */
const PageIconContext = createContext<LucideIcon | null>(null)

export const PageIconProvider = PageIconContext.Provider

export function usePageIcon(): LucideIcon | null {
  return useContext(PageIconContext)
}

/**
 * The section's icon in a tile of the accent before a page's title (decorative: the title names the
 * page). `tone="solid"` fills the tile with the accent for a record's card.
 */
export function PageIcon({ tone = 'tint', className }: { tone?: 'tint' | 'solid'; className?: string }) {
  const Icon = usePageIcon()
  if (!Icon) return null
  return (
    <span
      aria-hidden
      data-slot="page-icon"
      className={cn(
        'grid shrink-0 place-items-center rounded-panel',
        tone === 'solid'
          ? 'size-11 bg-linear-to-br from-stamp to-stamp/75 text-on-accent shadow-float sm:size-12 [&_svg]:size-5'
          : 'size-9 bg-stamp-tint text-stamp sm:size-10 [&_svg]:size-[18px] sm:[&_svg]:size-5',
        className,
      )}
    >
      <Icon />
    </span>
  )
}
